import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import DatePicker, {
  RenderInputProps,
  Day,
  CalendarProps,
  DayValue,
} from "@hassanmojab/react-modern-calendar-datepicker";
import "./index.scss";
import { TextField, TTextFieldProps } from "@/components/kit";
import SvgIcon from "@/components/common/SvgIcon.tsx";
import classNames from "classnames";
import { useOnClickOutside } from "@/hooks/general/useOnClickOutside";

export type TTextFieldDateProps = Omit<
  TTextFieldProps,
  "onChange" | "value"
> & {
  onChange: (e: string, data: DayValue) => void;
  value: string;
  displayFormat?: string;
  calenderProps?: CalendarProps<DayValue>;
  clearable?: boolean;
  wrapperClassName?: string;
  minimumDate?: {
    year: number;
    month: number;
    day: number;
  };
  maximumDate?: {
    year: number;
    month: number;
    day: number;
  };
};

const TextFieldDate = (props: TTextFieldDateProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useOnClickOutside(containerRef, () => {
    setIsOpen(false);
  });

  const {
    value,
    onChange = () => null,
    displayFormat = "MMMM, DD, YYYY",
    calenderProps = {},
    clearable,
    wrapperClassName = "",
    minimumDate,
    maximumDate,
    ...restProps
  } = props;

  const onDateChange = (date: Day) => {
    const { month, day, year } = date;
    const isoDate = moment({
      year,
      month: month - 1,
      day,
    });
    setIsOpen(false);
    onChange(isoDate.toISOString(), date);
  };

  const localValue = useMemo(() => {
    if (!value) return null;
    const m = moment(value);
    const result = {
      day: m.date(),
      month: m.month() + 1,
      year: m.year(),
    };
    return result;
  }, [value]);

  const displayValue = useMemo(() => {
    if (!value) return;
    return moment(value).format(displayFormat);
  }, [value]);

  const CustomInput = ({ ref }: RenderInputProps) => (
    <TextField
      {...restProps}
      endAdornment={
        <div className={"flex gap-2 items-center"}>
          {!clearable && value && (
            <SvgIcon
              onClick={() => onChange("", {} as DayValue)}
              className={"w-5 h-5 cursor-pointer"}
              iconName={"close"}
            />
          )}
          {restProps.endAdornment}
        </div>
      }
      value={displayValue}
      onChange={() => null}
      readOnly
      ref={ref}
      onClick={() => {
        if (!isOpen) {
          setIsOpen(true);
        }
      }}
    />
  );
  useEffect(() => {
    const handleBlur = () => setIsOpen(false);

    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  return (
    <div className="rangePicker" ref={containerRef}>
      <div
        className={classNames("fixed inset-0 bg-black bg-opacity-50 z-40", {
          hidden: !isOpen,
        })}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(false);
        }}
      />
      <div
        className={classNames("relative rangePicker", {
          "z-50": isOpen,
        })}
      >
        <div>
          <DatePicker
            calendarSelectedDayClassName={"!bg-gray-600"}
            wrapperClassName={classNames("w-full", wrapperClassName)}
            calendarClassName={classNames({
              hidden: !isOpen,
            })}
            value={localValue}
            onChange={onDateChange}
            minimumDate={minimumDate}
            maximumDate={maximumDate}
            renderInput={CustomInput}
            {...calenderProps}
          />
        </div>
      </div>
    </div>
  );
};

export default TextFieldDate;
